import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  Paperclip,
  RefreshCw,
  Search,
  Send,
  SquarePen,
  User,
  Users,
  Video,
} from 'lucide-react';

import { ENABLE_TEAMS_CHANNELS } from '../../config/features';
import { useWebSocket } from '../../context/WebSocketProvider';
import {
  deleteChatMessage,
  editChatMessage,
  listChatMessages,
  listChats,
  markChatRead,
  reactToMessage,
  sendChatAttachment,
  sendChatReply,
  suggestChatReply,
} from '../../services/teamsDmApi';
import { confirmDialog } from '../ConfirmDialog';
import ChannelsView from './ChannelsView';
import MessageBubble, { SystemEvent, when } from './MessageBubble';
import MeetingDetailModal from '../meeting/MeetingDetailModal';
import NewChat from './NewChat';
import { listTime } from '../../utils/helper';

// A value the user has not decided yet: [in brackets, with a question?].
const UNFILLED = /\[[^\]]*\?[^\]]*\]/;
// Same limit as the backend.
export const MAX_REPLY_CHARS = 4000;

const FILE_NOTES = {
  FILES_NEED_ONEDRIVE:
    'Images can be sent now. For other files, connect SharePoint in Integrations with My OneDrive (read and edit).',
};

const CONNECTION_NOTES = {
  NOT_CONNECTED: 'Connect Teams in Integrations to see your chats.',
  RECONNECT_REQUIRED:
    'Reconnect Teams in Integrations to allow reading and answering chats.',
};

const Note = ({ children }) => (
  <p className="px-3 py-6 text-center text-[13px] text-zinc-400">{children}</p>
);

const Failure = ({ error, onRetry }) => {
  const known = CONNECTION_NOTES[error?.code];
  return (
    <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
      <p className="text-[13px] text-zinc-400">
        {known || error?.message || 'Could not load Teams chats.'}
      </p>
      {!known && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg border border-zinc-700 px-3 py-1 text-[12px] text-zinc-200 hover:bg-zinc-800"
        >
          Try again
        </button>
      )}
    </div>
  );
};

const TYPE_ICONS = { direct: User, group: Users, meeting: Video };

const ChatList = ({ chats, onOpen }) => (
  <ul className="flex flex-col">
    {chats.map((chat) => {
      const Icon = TYPE_ICONS[chat.type] || User;
      return (
        <li key={chat.id}>
          <button
            type="button"
            onClick={() => onOpen(chat)}
            className="flex w-full items-start gap-2 border-b border-zinc-800 px-3 py-2.5 text-left hover:bg-zinc-800/50"
          >
            <Icon
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500"
              aria-label={chat.type}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex items-center justify-between gap-2">
                <span
                  className={`truncate text-[13px] ${chat.unread ? 'font-semibold text-white' : 'font-medium text-zinc-100'}`}
                >
                  {chat.with || 'Unknown'}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {chat.waiting && (
                    <span className="rounded-full bg-[#5652CF]/25 px-2 py-0.5 text-[10px] font-semibold text-indigo-200">
                      Your turn
                    </span>
                  )}
                  {chat.unread && (
                    <span
                      className="h-2 w-2 rounded-full bg-[#5652CF]"
                      aria-label="Unread"
                    />
                  )}
                  <span
                    className="text-[11px] text-zinc-500"
                    title={when(chat.last_message_at)}
                  >
                    {listTime(chat.last_message_at)}
                  </span>
                </span>
              </span>
              <span
                className={`truncate text-[12px] ${chat.unread ? 'text-zinc-200' : 'text-zinc-400'}`}
              >
                {chat.last_from_me
                  ? 'You: '
                  : chat.type !== 'direct' && chat.last_from
                    ? `${chat.last_from}: `
                    : ''}
                {chat.last_message || 'No messages yet'}
              </span>
            </span>
          </button>
        </li>
      );
    })}
  </ul>
);

/**
 * One chat: its messages (older ones on demand), reactions, a suggested
 * reply, and the reply box. "Reply" on the suggestion puts it in the box;
 * nothing is sent until the user presses Send.
 */
const ChatView = ({ accessToken, chat, onBack, onSent, onRead, liveTick }) => {
  const [messages, setMessages] = useState(null);
  const [summary, setSummary] = useState(null);
  const [older, setOlder] = useState(null); // cursor for the page before
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);
  const [suggestion, setSuggestion] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(null);
  const box = useRef(null);
  const end = useRef(null);
  // Bumped to load the chat again.
  const [round, setRound] = useState(0);

  useEffect(() => {
    let current = true;
    setError(null);
    setSuggestion('');
    listChatMessages(accessToken, chat.id)
      .then((res) => {
        if (!current) return;
        const rows = res?.data?.messages || [];
        // A live reload keeps the older pages already loaded.
        setMessages((prev) => {
          const newest = new Set(rows.map((m) => m.id));
          const kept = (prev || []).filter(
            (m) => !newest.has(m.id) && m.sent_at < (rows[0]?.sent_at || ''),
          );
          return [...kept, ...rows];
        });
        setOlder((prev) => (prev === null ? res?.data?.next || null : prev));
        // Opening a chat reads it, as in Teams.
        if (chat.unread) {
          markChatRead(accessToken, chat.id)
            .then(() => onRead?.(chat.id))
            .catch(() => {});
        }
        // A reply is suggested only when the last word is theirs.
        const last = rows.filter((m) => !m.system).pop();
        if (!last || last.from_me) return;
        setSuggesting(true);
        suggestChatReply(accessToken, chat.id)
          .then((out) => current && setSuggestion(out?.data?.text || ''))
          // No suggestion: the reply box still works.
          .catch(() => {})
          .finally(() => current && setSuggesting(false));
      })
      .catch((err) => {
        if (!current) return;
        setMessages([]);
        setError(err || {});
      });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, chat.id, round, liveTick]);

  useEffect(() => {
    if (!loadingOlder) end.current?.scrollIntoView?.({ block: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const loadOlder = async () => {
    if (!older || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await listChatMessages(accessToken, chat.id, older);
      const rows = res?.data?.messages || [];
      setMessages((prev) => {
        const have = new Set((prev || []).map((m) => m.id));
        return [...rows.filter((m) => !have.has(m.id)), ...(prev || [])];
      });
      setOlder(res?.data?.next || null);
    } catch (err) {
      toast.error('Could not load older messages.');
    } finally {
      setLoadingOlder(false);
    }
  };

  const react = async (message, reaction, on) => {
    const update = (rows) =>
      rows.map((m) => {
        if (m.id !== message.id) return m;
        const others = (m.reactions || []).filter((r) => r.type !== reaction);
        const current = (m.reactions || []).find((r) => r.type === reaction);
        const count = (current?.count || 0) + (on ? 1 : -1);
        return {
          ...m,
          reactions:
            count > 0
              ? [...others, { type: reaction, count, mine: on }]
              : others,
        };
      });
    setMessages((rows) => update(rows || []));
    try {
      await reactToMessage(accessToken, chat.id, message.id, reaction, on);
    } catch (err) {
      setMessages((rows) => update(rows || []).map((m) => m)); // keep; reload fixes it
      toast.error('Teams did not take the reaction.');
      setRound((n) => n + 1);
    }
  };

  const editMessage = async (message, newText) => {
    try {
      await editChatMessage(accessToken, chat.id, message.id, newText);
      setMessages((rows) =>
        (rows || []).map((m) =>
          m.id === message.id
            ? { ...m, text: newText, html: null, edited: true }
            : m,
        ),
      );
      return true;
    } catch (err) {
      toast.error(`Could not edit: ${err?.message || 'try again'}`);
      return false;
    }
  };

  const deleteMessage = async (message) => {
    const ok = await confirmDialog({
      title: 'Delete this message?',
      message:
        'It is removed for everyone in the chat. You can still undo it in Teams.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteChatMessage(accessToken, chat.id, message.id);
      setMessages((rows) => (rows || []).filter((m) => m.id !== message.id));
    } catch (err) {
      toast.error(`Could not delete: ${err?.message || 'try again'}`);
    }
  };

  const fileInput = useRef(null);
  const [attaching, setAttaching] = useState(false);
  const attach = async (file) => {
    if (!file) return;
    setAttaching(true);
    setNote(null);
    try {
      await sendChatAttachment(accessToken, chat.id, file, text.trim());
      setText('');
      setSuggestion('');
      setNote(`Sent ${file.name}.`);
      onSent?.(chat.id, text.trim() || file.name, new Date().toISOString());
      setRound((n) => n + 1);
    } catch (err) {
      if (FILE_NOTES[err?.code]) setNote(FILE_NOTES[err.code]);
      else
        toast.error(`Could not send the file: ${err?.message || 'try again'}`);
    } finally {
      setAttaching(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const value = text.trim();
  const unfilled = UNFILLED.test(value);
  const tooLong = value.length > MAX_REPLY_CHARS;
  const blocked = !value || unfilled || tooLong || sending;

  const fillReply = () => {
    setText(suggestion);
    setNote(null);
    box.current?.focus();
  };

  const send = async () => {
    if (blocked) return;
    setSending(true);
    setNote(null);
    try {
      const res = await sendChatReply(accessToken, chat.id, value);
      const sentAt = res?.data?.sent_at || new Date().toISOString();
      setMessages((rows) => [
        ...(rows || []),
        {
          id: res?.data?.id || `sent-${sentAt}`,
          from: 'You',
          from_me: true,
          text: value,
          sent_at: sentAt,
        },
      ]);
      setText('');
      setSuggestion('');
      setNote('Sent.');
      onSent?.(chat.id, value, sentAt);
    } catch (err) {
      // The text stays in the box so nothing typed is lost.
      if (CONNECTION_NOTES[err?.code]) setNote(CONNECTION_NOTES[err.code]);
      else toast.error(`Could not send: ${err?.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-2">
        <button
          type="button"
          aria-label="Back to chats"
          onClick={onBack}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-zinc-100">
          {chat.with || 'Unknown'}
        </span>
        {chat.web_url && (
          <a
            href={chat.web_url}
            target="_blank"
            rel="noreferrer"
            aria-label="Open in Teams"
            title="Open in Teams"
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <button
          type="button"
          aria-label="Refresh chat"
          onClick={() => setRound((n) => n + 1)}
          className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {older && (
          <div className="mb-2 flex justify-center">
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingOlder}
              className="rounded-full border border-zinc-700 px-3 py-0.5 text-[11px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              {loadingOlder ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        )}
        {messages === null && <Note>Loading messages…</Note>}
        {error && (
          <Failure error={error} onRetry={() => setRound((n) => n + 1)} />
        )}
        {messages !== null && !error && messages.length === 0 && (
          <Note>No messages in this chat yet.</Note>
        )}
        <ul className="flex flex-col gap-2" aria-label="Messages">
          {(messages || []).map((message) =>
            message.system ? (
              <SystemEvent
                key={message.id}
                message={message}
                title={chat.with}
                teamsUrl={chat.web_url}
                onOpenSummary={setSummary}
              />
            ) : (
              <MessageBubble
                key={message.id}
                accessToken={accessToken}
                chatId={chat.id}
                message={message}
                onReact={(reaction, on) => react(message, reaction, on)}
                onEdit={(newText) => editMessage(message, newText)}
                onDelete={() => deleteMessage(message)}
              />
            ),
          )}
        </ul>
        {summary && (
          <MeetingDetailModal
            eventId={summary}
            onClose={() => setSummary(null)}
          />
        )}
        <div ref={end} />
      </div>

      {!error && messages !== null && (
        <div className="shrink-0 border-t border-zinc-800 px-3 py-2">
          {suggesting && (
            <p className="mb-2 flex items-center gap-1.5 text-[12px] text-zinc-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Writing a suggested reply…
            </p>
          )}
          {suggestion && (
            <div className="mb-2 rounded-lg border border-zinc-700 bg-zinc-900 p-2.5">
              <p className="mb-1 text-[11px] font-semibold text-zinc-400">
                Suggested reply
              </p>
              <p
                data-testid="suggested-reply"
                className="whitespace-pre-wrap text-[13px] text-zinc-100"
              >
                {suggestion}
              </p>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={fillReply}
                  className="rounded-lg bg-[#5652CF] px-3 py-1 text-[12px] font-semibold text-white hover:brightness-110"
                >
                  Reply
                </button>
              </div>
            </div>
          )}
          <textarea
            ref={box}
            aria-label="Your reply"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setNote(null);
            }}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter is a new line (as in Teams).
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={3}
            placeholder="Write a reply"
            disabled={sending}
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900 p-2 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF] disabled:opacity-60"
          />
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <p className="text-[12px] text-zinc-400" role="status">
              {unfilled
                ? 'Fill in or remove the values in [brackets] before sending.'
                : tooLong
                  ? `Keep the reply under ${MAX_REPLY_CHARS} characters.`
                  : note || ''}
            </p>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              aria-label="Attach a file"
              onChange={(e) => attach(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={attaching || sending}
              title="Send an image or file (the text above goes with it)"
              aria-label="Attach"
              className="ml-auto shrink-0 rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40"
            >
              {attaching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Paperclip className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={send}
              disabled={blocked}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {sending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The signed-in member's own Teams chats (1:1, group, meeting) and channels.
 * Pick a chat to read it, react, take the suggested reply or write one, and
 * send it. Changes in Teams arrive live (the member's own socket room).
 */
const TeamsDmPanel = ({ accessToken }) => {
  const [chats, setChats] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [view, setView] = useState('chats'); // chats | channels | new
  const [query, setQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [liveTick, setLiveTick] = useState(0);
  const { subscribe, isConnected } = useWebSocket() || {};
  const openRef = useRef(null);
  openRef.current = open;

  const load = useCallback(
    (quiet = false) => {
      if (!accessToken) return;
      setError(null);
      if (!quiet) setChats(null);
      listChats(accessToken)
        .then((res) => setChats(res?.data || []))
        .catch((err) => {
          if (quiet) return;
          setChats([]);
          setError(err || {});
        });
    },
    [accessToken],
  );

  useEffect(() => {
    load();
  }, [load]);

  // A chat changed in Teams (new message, edit, reaction): reload the list,
  // and the open chat if it is that one. The event carries only the chat id.
  useEffect(() => {
    if (!subscribe || !isConnected) return undefined;
    return subscribe({
      teams_chat_changed: (data) => {
        load(true);
        if (openRef.current && openRef.current.id === data?.chat_id) {
          setLiveTick((n) => n + 1);
        }
      },
    });
  }, [subscribe, isConnected, load]);

  // Keep the list in step with a reply sent from here.
  const markSent = (chatId, text, sentAt) =>
    setChats((rows) =>
      (rows || []).map((row) =>
        row.id === chatId
          ? {
              ...row,
              last_message: text.slice(0, 280),
              last_message_at: sentAt,
              last_from_me: true,
              waiting: false,
              unread: false,
            }
          : row,
      ),
    );
  const markRead = (chatId) =>
    setChats((rows) =>
      (rows || []).map((row) =>
        row.id === chatId ? { ...row, unread: false } : row,
      ),
    );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (chats || []).filter(
      (chat) =>
        (!unreadOnly || chat.unread) &&
        (!needle ||
          (chat.with || '').toLowerCase().includes(needle) ||
          (chat.last_message || '').toLowerCase().includes(needle)),
    );
  }, [chats, query, unreadOnly]);
  const unreadCount = (chats || []).filter((c) => c.unread).length;

  if (open) {
    return (
      <ChatView
        key={open.id}
        accessToken={accessToken}
        chat={open}
        // Live events keep the list current; no refetch just for going back.
        onBack={() => setOpen(null)}
        onSent={markSent}
        onRead={markRead}
        liveTick={liveTick}
      />
    );
  }
  if (view === 'new') {
    return (
      <NewChat
        accessToken={accessToken}
        onBack={() => setView('chats')}
        onStarted={(chatId, name, type) => {
          setView('chats');
          load(true);
          if (chatId) setOpen({ id: chatId, with: name, type });
        }}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-800 px-3 py-2">
        <div className="flex items-center gap-1 rounded-lg bg-zinc-800/60 p-0.5">
          {[
            ['chats', unreadCount ? `Chats (${unreadCount})` : 'Chats'],
            ...(ENABLE_TEAMS_CHANNELS ? [['channels', 'Channels']] : []),
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`rounded-md px-2.5 py-1 text-[11px] font-medium ${view === key ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {view === 'chats' && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="New chat"
              title="New chat"
              onClick={() => setView('new')}
              className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <SquarePen className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Refresh chats"
              onClick={() => load()}
              className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {view === 'channels' ? (
        <ChannelsView accessToken={accessToken} />
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2 border-b border-zinc-800 px-3 py-1.5">
            <Search className="h-3 w-3 shrink-0 text-zinc-500" />
            <input
              aria-label="Search chats"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search chats"
              className="min-w-0 flex-1 bg-transparent text-[12px] text-zinc-200 outline-none placeholder:text-zinc-600"
            />
            <button
              type="button"
              aria-pressed={unreadOnly}
              onClick={() => setUnreadOnly((u) => !u)}
              className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${unreadOnly ? 'border-transparent bg-[#5652CF] text-white' : 'border-zinc-700 text-zinc-400'}`}
            >
              Unread
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {chats === null && <Note>Loading chats…</Note>}
            {error && <Failure error={error} onRetry={() => load()} />}
            {chats !== null && !error && shown.length === 0 && (
              <Note>
                {chats.length ? 'No chats match.' : 'No Teams chats yet.'}
              </Note>
            )}
            {shown.length > 0 && <ChatList chats={shown} onOpen={setOpen} />}
          </div>
        </>
      )}
    </div>
  );
};

export default TeamsDmPanel;
